import type { Trait, LifeChoice, WorldDetail, Dossier, Relationship } from "../types";
import type { NarrativeContext, NarrativeResult } from "../narrative";
import {
  fill,
  pick,
  pickDistinct,
  clamp,
  cap,
  lower,
  listJoin,
  INTEREST_PHRASE,
  STRENGTH_PHRASE,
  AMBITION_PHRASE,
  LIFE_PATH_PHRASE,
  influenceLead,
} from "./helpers";
import { generatePersonName } from "./names";
import { buildUniverseDescription, buildWorldDetails, STRENGTH_TRAIT_MAP } from "./shared";
import {
  TITLE_PREFIXES,
  TITLE_OBJECTS,
  TAGLINE_PART_A,
  TAGLINE_PART_B,
  OCCUPATION_ROLES,
  OCCUPATION_MODIFIERS,
  FORK_TEMPLATES,
  ORIGIN_CHILDHOOD,
  ORIGIN_CURIOSITY,
  ORIGIN_FORMATION,
  TURNING_CRISIS,
  TURNING_CHOICE,
  TURNING_AFTERMATH,
  TODAY_DAWN,
  TODAY_WORK,
  TODAY_EVENING,
  CHOICE_BANK_LOSS,
  CHOICE_BANK_OPPORTUNITY,
  CHOICE_BANK_CONVICTION,
  DIVERGENT_WORLD_EXTRAS,
  DOSSIER_SKILLS_BASE,
  DOSSIER_MOTIVATIONS,
  DOSSIER_ACHIEVEMENTS,
  DOSSIER_FAILURES,
  DOSSIER_DILEMMAS,
  DOSSIER_TRAJECTORY_NEAR,
  DOSSIER_TRAJECTORY_LONG,
  REGIONAL_HUMAN_PLACES,
  DISTANT_METROPOLISES,
} from "./divergent.banks";

/**
 * Divergent: grounded, human-scale reality in our physics where one early decision
 * went the other way. Real-feeling jobs, towns, families, anywhere in the world.
 * Built via combinatorial recombination across independent seeded streams.
 */
export function generateDivergent(ctx: NarrativeContext): NarrativeResult {
  const { name: visitorName, axes, condition, universe, sharedUniverse, resolved } = ctx;

  // Track used names to guarantee no internal repeats
  const usedNames = new Set<string>();
  if (visitorName && visitorName.trim().length > 0) {
    usedNames.add(visitorName.trim());
  }

  // 1. Personal Name (worldwide pool, culturally consistent)
  const personalName = generatePersonName(ctx.stream("name"), usedNames);
  usedNames.add(personalName);

  // 2. Setting / Geography
  const placeInfo = pick(REGIONAL_HUMAN_PLACES, ctx.stream("geography"));
  const distantCity = pick(DISTANT_METROPOLISES, ctx.stream("distant_city"));

  // 3. Title / Epithet (Title Prefix + Title Object)
  const titlePrefix = pick(TITLE_PREFIXES, ctx.stream("title_prefix"));
  const titleObject = pick(TITLE_OBJECTS, ctx.stream("title_object"));
  const title = `${titlePrefix} ${titleObject}`;

  // 4. Tagline (Part A + Part B: >= 40 combinations)
  const taglineA = pick(TAGLINE_PART_A, ctx.stream("tagline_a"));
  const taglineB = pick(TAGLINE_PART_B, ctx.stream("tagline_b"));
  const tagline = `${taglineA} ${taglineB}`;

  // 5. Occupation: Domain (from ALL of ctx.resolved.interests) x Setting/Seniority (from ctx.resolved.lifePath)
  // >= 6 role variants per interest, >= 4 variants per life path
  const interestRoles: string[] = [];
  for (const intId of resolved.interests) {
    const rolesForInt = OCCUPATION_ROLES[intId] || OCCUPATION_ROLES.science;
    interestRoles.push(pick(rolesForInt, ctx.stream(`occ_role_${intId}`)));
  }
  const chosenRole = pick(interestRoles, ctx.stream("occ_role_pick"));

  const pathModifiers = OCCUPATION_MODIFIERS[resolved.lifePath] || OCCUPATION_MODIFIERS.rooted;
  const chosenModifier = pick(pathModifiers, ctx.stream("occ_mod_pick"));

  // Format naturally: e.g. "Senior Soil Hydrologist" or "Founding Director of the Regional Watershed Guild"
  let occupation = "";
  if (chosenModifier && chosenModifier.endsWith(" the")) {
    occupation = `${chosenModifier} ${chosenRole || "Practitioner"} Society`;
  } else if (chosenModifier) {
    occupation = `${chosenModifier} ${chosenRole || "Practitioner"}`;
  } else {
    occupation = chosenRole || "Practitioner";
  }

  // 6. Traits: Exactly 4 traits drawn from all resolved.strengths via STRENGTH_TRAIT_MAP
  // Level from matching axis +- jitter, clamped 25..95
  const traits: Trait[] = [];
  const chosenStrengths = [...resolved.strengths];
  // Ensure we have at least 4 by cycling if needed
  while (chosenStrengths.length < 4) {
    chosenStrengths.push(chosenStrengths[chosenStrengths.length % resolved.strengths.length]);
  }

  const traitRng = ctx.stream("traits");
  const usedTraitNames = new Set<string>();

  for (let i = 0; i < 4; i++) {
    const sId = chosenStrengths[i];
    const bank = STRENGTH_TRAIT_MAP[sId] || STRENGTH_TRAIT_MAP.discipline;
    const available = bank.filter((item) => !usedTraitNames.has(item.name));
    const pickedItem = available.length > 0 ? pick(available, traitRng) : pick(bank, traitRng);
    usedTraitNames.add(pickedItem.name);

    // Map strength to axis
    let baseLevel = axes.curiosity;
    if (sId === "discipline" || sId === "patience") baseLevel = axes.stability;
    else if (sId === "empathy" || sId === "humor") baseLevel = axes.connection;
    else if (sId === "courage") baseLevel = axes.courage;
    else if (sId === "logic" || sId === "curiosity") baseLevel = axes.curiosity;
    else if (sId === "creativity") baseLevel = axes.influence;

    const jitter = Math.floor(traitRng() * 14) - 7;
    traits.push({
      name: pickedItem.name,
      description: pickedItem.description,
      level: clamp(baseLevel + jitter, 25, 95),
    });
  }

  // 7. Formative Early Fork & Story
  const primaryInterest = resolved.interests[0];
  const interestPhrases = INTEREST_PHRASE[primaryInterest];
  const strengthPhrases = STRENGTH_PHRASE[resolved.strengths[0]];
  const ambitionPhrases = AMBITION_PHRASE[resolved.ambition];
  const lifePathPhrases = LIFE_PATH_PHRASE[resolved.lifePath];

  const storyVars: Record<string, string> = {
    name: personalName,
    visitorName: visitorName || personalName,
    homeRegion: placeInfo.region,
    homePlace: placeInfo.town,
    localPlace: placeInfo.town,
    localFeature: placeInfo.feature,
    distantCity,
    domainNoun: interestPhrases.noun,
    domainField: interestPhrases.field,
    domainVerb: interestPhrases.verb,
    domainSetting: lifePathPhrases.setting,
    domainPractice: interestPhrases.humanDomain,
    strengthAdj: strengthPhrases.adj,
    strengthHabit: strengthPhrases.habit,
    strengthNoun: strengthPhrases.noun,
    ambitionGoal: ambitionPhrases.goal,
    ambitionDrive: ambitionPhrases.drive,
    lifePathRole: lifePathPhrases.role,
    lifePathManner: lifePathPhrases.manner,
    occupation,
  };

  // Story: Origin (3 parts assembled with fill)
  const originPart1 = fill(pick(ORIGIN_CHILDHOOD, ctx.stream("origin_part1")), storyVars);
  const originPart2 = fill(pick(ORIGIN_CURIOSITY, ctx.stream("origin_part2")), storyVars);
  const originPart3 = fill(pick(ORIGIN_FORMATION, ctx.stream("origin_part3")), storyVars);
  const origin = `${originPart1} ${originPart2} ${originPart3}`;

  // Story: Turning Point (Fork chosen from FORK_TEMPLATES + Crisis + Choice + Aftermath)
  const forkTemplate = pick(FORK_TEMPLATES, ctx.stream("fork_template"));
  const specificFork = fill(forkTemplate.template, storyVars);
  const turnCrisis = fill(pick(TURNING_CRISIS, ctx.stream("turn_crisis")), storyVars);
  const turnChoice = fill(pick(TURNING_CHOICE, ctx.stream("turn_choice")), storyVars);
  const turnAftermath = fill(pick(TURNING_AFTERMATH, ctx.stream("turn_aftermath")), storyVars);
  const turningPoint = `${specificFork} ${turnCrisis} ${turnChoice} ${turnAftermath}`;

  // Story: Today (3 parts assembled with fill)
  const todayDawn = fill(pick(TODAY_DAWN, ctx.stream("today_dawn")), storyVars);
  const todayWork = fill(pick(TODAY_WORK, ctx.stream("today_work")), storyVars);
  const todayEvening = fill(pick(TODAY_EVENING, ctx.stream("today_evening")), storyVars);
  const today = `${todayDawn} ${todayWork} ${todayEvening}`;

  // 8. Choices: Exactly 3 distinct kinds (a loss, an opportunity, a conviction/test)
  const lossChoice = pick(CHOICE_BANK_LOSS, ctx.stream("choice_loss"));
  const oppChoice = pick(CHOICE_BANK_OPPORTUNITY, ctx.stream("choice_opp"));
  const convChoice = pick(CHOICE_BANK_CONVICTION, ctx.stream("choice_conv"));

  const rawChoices = [lossChoice, oppChoice, convChoice];
  const choices: LifeChoice[] = rawChoices.map((c) => ({
    age: c.age,
    choice: fill(c.choice, storyVars),
    roadNotTaken: fill(c.roadNotTaken, storyVars),
    consequence: fill(c.consequence, storyVars),
  }));

  // 9. World Details: Extra Details Pool (3 picked from >= 12 variants)
  const extraPicks = pickDistinct(DIVERGENT_WORLD_EXTRAS, 3, ctx.stream("world_extras"));
  const interestDetail = `Organizing daily community labor around ${listJoin(
    resolved.interests.map((id) => INTEREST_PHRASE[id].noun)
  )}, preserved through local apprenticeships.`;

  const worldDetails: WorldDetail[] = buildWorldDetails(
    "divergent",
    universe,
    condition,
    interestDetail,
    extraPicks as unknown as { label: string; value: string }[]
  );

  const universeDescription = buildUniverseDescription(universe, condition, sharedUniverse);

  // 10. Dossier:
  // - background: 2-4 sentences
  const background = `${personalName} was born in ${placeInfo.town} along ${placeInfo.region}, where generations of the family had worked in trade and local transport. Educated in provincial public schools and early vocational workshops, ${personalName} learned the value of practical self-reliance before reaching adulthood. Rather than chasing credentials in distant metropolitan towers, ${personalName} chose an immersive local apprenticeship, mastering the demands of ${interestPhrases.humanDomain} through daily physical practice.`;

  // - personality: 2-3 sentences, specific
  const personality = `Known for a ${strengthPhrases.adj} demeanor and a habit of ${strengthPhrases.habit}, ${personalName} speaks softly in public forums but acts with decisive moral clarity when core principles are at stake. Neighbors value their unhurried, measured composure, especially when seasonal emergencies or bureaucratic disputes create local panic.`;

  // - skills: 4-6 short skill phrases (from strengths + occupation)
  const skillRng = ctx.stream("skills");
  const baseSkills = pickDistinct(DOSSIER_SKILLS_BASE, 3, skillRng);
  const roleLabel = chosenRole || occupation || "Specialist";
  const specializedSkills = [
    `${roleLabel} diagnostics`,
    `${cap(strengthPhrases?.noun || "clarity")} in crisis negotiation`,
    `${placeInfo.town} district logistics coordination`,
  ];
  const skills = [...baseSkills, ...specializedSkills].slice(0, 5);

  // - motivations: 2-3 sentences/phrases (from ambition)
  const motivationRng = ctx.stream("motivations");
  const pickedMotivations = pickDistinct(DOSSIER_MOTIVATIONS, 2, motivationRng);
  const customMotivation = `Pursuing a life dedicated to ${ambitionPhrases.goal}, ensuring that immediate communal bonds take precedence over external accolades.`;
  const motivations = [...pickedMotivations, customMotivation].slice(0, 3);

  // - relationships: exactly 3 (distinct roles, names from generatePersonName with avoid set)
  const relRng = ctx.stream("relationships");
  const rel1Name = generatePersonName(relRng, usedNames);
  usedNames.add(rel1Name);
  const rel2Name = generatePersonName(relRng, usedNames);
  usedNames.add(rel2Name);
  const rel3Name = generatePersonName(relRng, usedNames);
  usedNames.add(rel3Name);

  const relationships: Relationship[] = [
    {
      who: rel1Name,
      role: "Senior Mentor & Master Craftsman",
      bond: `${rel1Name} took in ${personalName} as an outspoken teenager, instilling the exacting standards of ${interestPhrases.humanDomain}. Though now retired to an orchard cottage, they still consult on difficult civic restoration projects.`,
    },
    {
      who: rel2Name,
      role: "Professional Rival & Council Delegate",
      bond: `A former classmate who took the corporate engineering track before returning as a municipal commissioner. While they dispute budget allocations and regulatory red tape vigorously, both hold profound personal respect for the other's dedication to ${placeInfo.town}.`,
    },
    {
      who: rel3Name,
      role: "Lifelong Companion & Workshop Partner",
      bond: `Having stood side by side through the bitter flood season and early workshop debt, ${rel3Name} manages the daily logistics while ${personalName} handles field assessments. Their unshakeable mutual loyalty is the unspoken backbone of the entire operation.`,
    },
  ];

  // - achievements: 2-3 sentences
  const achRng = ctx.stream("achievements");
  const achievements = pickDistinct(DOSSIER_ACHIEVEMENTS, 2, achRng);

  // - failures: exactly 2 sentences
  const failRng = ctx.stream("failures");
  const failures = pickDistinct(DOSSIER_FAILURES, 2, failRng);

  // - dilemma: 2-3 sentences, an open tension
  const dilemma = pick(DOSSIER_DILEMMAS, ctx.stream("dilemma"));

  // - trajectory: nearFuture + longArc (each 1-3 sentences)
  const nearTemplate = pick(DOSSIER_TRAJECTORY_NEAR, ctx.stream("traj_near"));
  const longTemplate = pick(DOSSIER_TRAJECTORY_LONG, ctx.stream("traj_long"));
  const trajectory = {
    nearFuture: fill(typeof nearTemplate === "string" ? nearTemplate : "", storyVars),
    longArc: fill(typeof longTemplate === "string" ? longTemplate : "", storyVars),
  };

  // - influence: [...influenceLead(ctx), then 2-3 sentences specific to this self]
  const baseInfluence = influenceLead(ctx);
  const specificInfluence: string[] = [
    `Your selected trade of ${listJoin(
      resolved.interests.map((id) => INTEREST_PHRASE[id].noun)
    )} defined this self's craft as ${occupation}, keeping labor tethered to physical reality.`,
    `The life path walk of "${lifePathPhrases.role}" combined with an ambition to "${ambitionPhrases.goal}" directly shaped the turning point where ${personalName} turned down distant prospects to anchor in ${placeInfo.town}.`,
    `Core strengths of ${listJoin(
      resolved.strengths.map((id) => STRENGTH_PHRASE[id].noun)
    )} dictated ${personalName}'s daily temperament and the patient, restorative focus of their workshop.`,
  ];
  const influence = [...baseInfluence, ...specificInfluence];

  const dossier: Dossier = {
    background,
    personality,
    skills,
    motivations,
    relationships,
    achievements,
    failures,
    dilemma,
    trajectory,
    influence,
  };

  return {
    name: personalName,
    title,
    tagline,
    occupation,
    traits,
    story: { origin, turningPoint, today },
    choices,
    worldDetails,
    universeDescription,
    dossier,
  };
}
